/**
 * Ashford PDP — main-image slider + Figma-sized thumbs
 * (102px desktop / 56px mobile). Thumbs use native horizontal scroll
 * so they can be dragged; arrows stay as a secondary control.
 */
(function () {
  function bindZoom(root) {
    const stage = root.querySelector('.ashford-product__stage');
    if (!stage || stage.dataset.ashfordZoomReady === 'true') return;
    stage.dataset.ashfordZoomReady = 'true';

    function setOrigin(clientX, clientY) {
      const rect = stage.getBoundingClientRect();
      const x = ((clientX - rect.left) / rect.width) * 100;
      const y = ((clientY - rect.top) / rect.height) * 100;
      stage.style.setProperty('--ashford-zoom-x', x + '%');
      stage.style.setProperty('--ashford-zoom-y', y + '%');
    }

    // Click (mouse) or tap (touch) toggles zoom in/out, centered on the
    // clicked point — was previously hover-to-zoom on desktop (mouseenter/
    // mousemove/mouseleave), which zoomed just from moving the cursor over
    // the image with no click at all. Same tap-toggle pattern for both
    // input types now; ignored if the pointer moved enough to be a swipe/
    // drag rather than a click (handled by the slide-drag logic instead).
    let down = null;
    const MOVE_THRESHOLD = 10;

    stage.addEventListener('pointerdown', (event) => {
      down = { x: event.clientX, y: event.clientY };
    });

    stage.addEventListener('pointerup', (event) => {
      if (!down) return;
      const dx = event.clientX - down.x;
      const dy = event.clientY - down.y;
      const moved = Math.abs(dx) > MOVE_THRESHOLD || Math.abs(dy) > MOVE_THRESHOLD;
      down = null;
      if (moved) return;
      if (stage.classList.contains('is-zooming')) {
        stage.classList.remove('is-zooming');
      } else {
        setOrigin(event.clientX, event.clientY);
        stage.classList.add('is-zooming');
      }
    });

    // Once zoomed (via click/tap above), a mouse can still pan the zoomed
    // view by moving over the image — doesn't itself trigger zoom.
    stage.addEventListener('mousemove', (event) => {
      if (!stage.classList.contains('is-zooming')) return;
      setOrigin(event.clientX, event.clientY);
    });
  }

  function bindGallery(root) {
    const stageViewport = root.querySelector('[data-ashford-stage-viewport]');
    const stageTrack = root.querySelector('[data-ashford-stage-track]');
    const thumbs = Array.from(root.querySelectorAll('[data-ashford-thumb]'));
    const thumbTrack = root.querySelector('[data-ashford-thumbs]');
    const thumbViewport = root.querySelector('[data-ashford-thumbs-viewport]');
    const prev = root.querySelector('[data-ashford-thumbs-prev]');
    const next = root.querySelector('[data-ashford-thumbs-next]');
    const slides = stageTrack ? Array.from(stageTrack.children) : [];
    const count = slides.length || thumbs.length;
    if (!count) return;

    let current = 0;
    let suppressThumbClick = false;

    function thumbStep() {
      const item = thumbTrack && thumbTrack.querySelector('.ashford-product__thumb-item');
      if (!item || !thumbTrack) return 0;
      const styles = window.getComputedStyle(thumbTrack);
      const gap = parseFloat(styles.columnGap || styles.gap) || 0;
      return item.getBoundingClientRect().width + gap;
    }

    function maxThumbScroll() {
      if (!thumbViewport) return 0;
      return Math.max(0, thumbViewport.scrollWidth - thumbViewport.clientWidth);
    }

    function updateThumbArrows() {
      const max = maxThumbScroll();
      const left = thumbViewport ? thumbViewport.scrollLeft : 0;
      if (prev) prev.disabled = left <= 2;
      if (next) next.disabled = left >= max - 2;
    }

    function scrollThumbsBy(delta) {
      if (!thumbViewport) return;
      thumbViewport.scrollBy({ left: delta, behavior: 'smooth' });
    }

    function keepThumbInView(index) {
      const item = thumbs[index] && thumbs[index].closest('.ashford-product__thumb-item');
      if (!item || !thumbViewport) return;
      const vp = thumbViewport.getBoundingClientRect();
      const r = item.getBoundingClientRect();
      if (r.left < vp.left + 2) {
        thumbViewport.scrollBy({ left: r.left - vp.left, behavior: 'smooth' });
      } else if (r.right > vp.right - 2) {
        thumbViewport.scrollBy({ left: r.right - vp.right, behavior: 'smooth' });
      }
    }

    function goTo(index) {
      const stage = root.querySelector('.ashford-product__stage');
      if (stage) stage.classList.remove('is-zooming');
      current = Math.max(0, Math.min(count - 1, index));
      if (stageTrack) {
        stageTrack.style.transition = '';
        stageTrack.style.transform = `translateX(-${current * 100}%)`;
      }
      slides.forEach((slide, i) => {
        slide.classList.toggle('is-active', i === current);
      });
      thumbs.forEach((thumb, i) => {
        const on = i === current;
        thumb.classList.toggle('is-active', on);
        thumb.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      keepThumbInView(current);
      updateThumbArrows();
    }

    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        if (suppressThumbClick) return;
        const index = Number(thumb.getAttribute('data-index'));
        if (Number.isNaN(index)) return;
        goTo(index);
      });
    });

    if (prev) {
      prev.addEventListener('click', () => scrollThumbsBy(-thumbStep()));
    }
    if (next) {
      next.addEventListener('click', () => scrollThumbsBy(thumbStep()));
    }

    if (thumbViewport) {
      thumbViewport.addEventListener('scroll', updateThumbArrows, { passive: true });

      let drag = null;
      thumbViewport.addEventListener('pointerdown', (event) => {
        if (event.button !== 0) return;
        if (event.pointerType === 'touch') return;
        if (maxThumbScroll() <= 0) return;
        drag = {
          id: event.pointerId,
          startX: event.clientX,
          origin: thumbViewport.scrollLeft,
          moved: false,
          // Resolved now, before setPointerCapture below retargets every
          // later pointer event's event.target to thumbViewport itself —
          // reading it from the pointerup event in endThumbDrag would
          // always miss the actual thumb that was under the cursor.
          thumbEl: event.target.closest('[data-ashford-thumb]'),
        };
        thumbViewport.setPointerCapture(event.pointerId);
      });
      thumbViewport.addEventListener('pointermove', (event) => {
        if (!drag || event.pointerId !== drag.id) return;
        const dx = event.clientX - drag.startX;
        // 6px was tight enough that ordinary mouse/trackpad jitter between
        // mousedown and mouseup on a plain click could cross it, getting
        // misread as a drag and silently suppressing the click.
        if (Math.abs(dx) > 10) drag.moved = true;
        thumbViewport.scrollLeft = drag.origin - dx;
      });
      function endThumbDrag(event) {
        if (!drag || event.pointerId !== drag.id) return;
        const moved = drag.moved;
        const downThumbEl = drag.thumbEl;
        drag = null;
        if (moved) {
          suppressThumbClick = true;
          window.setTimeout(() => {
            suppressThumbClick = false;
          }, 80);
        } else {
          // Acted on directly here, rather than only relying on the
          // native click event that follows — once setPointerCapture has
          // been engaged (needed for the drag-to-scroll case above),
          // whether a synthesized click still reliably reaches the
          // captured-under element isn't consistent enough across
          // browsers to depend on for something the click listener below
          // is meant to guarantee. Uses drag.thumbEl (captured at
          // pointerdown, before capture retargeted event.target) rather
          // than event.target here.
          const thumbEl = downThumbEl;
          if (thumbEl) {
            const index = Number(thumbEl.getAttribute('data-index'));
            if (!Number.isNaN(index)) {
              suppressThumbClick = true;
              window.setTimeout(() => {
                suppressThumbClick = false;
              }, 80);
              goTo(index);
            }
          }
        }
        updateThumbArrows();
      }
      thumbViewport.addEventListener('pointerup', endThumbDrag);
      thumbViewport.addEventListener('pointercancel', endThumbDrag);
    }

    if (stageViewport && stageTrack && count > 1) {
      let drag = null;
      stageViewport.addEventListener('pointerdown', (event) => {
        if (event.button !== 0) return;
        drag = { startX: event.clientX, origin: current };
        stageViewport.setPointerCapture(event.pointerId);
      });
      stageViewport.addEventListener('pointermove', (event) => {
        if (!drag) return;
        const dx = event.clientX - drag.startX;
        const width = stageViewport.getBoundingClientRect().width || 1;
        stageTrack.style.transition = 'none';
        stageTrack.style.transform = `translateX(${-(drag.origin * width) + dx}px)`;
      });
      function endStageDrag(event) {
        if (!drag) return;
        const dx = event.clientX - drag.startX;
        const width = stageViewport.getBoundingClientRect().width || 1;
        const threshold = Math.max(40, width * 0.18);
        const origin = drag.origin;
        drag = null;
        stageTrack.style.transition = '';
        if (dx < -threshold) {
          goTo(origin + 1);
        } else if (dx > threshold) {
          goTo(origin - 1);
        } else {
          // Plain click/tap, not a swipe — snap the track back (pointermove
          // may have nudged its transform slightly) without calling the
          // full goTo(), which also unconditionally strips is-zooming and
          // was fighting with the zoom click-toggle in bindZoom: a second
          // click meant to zoom back out got its own class removal
          // immediately undone because this handler had already stripped
          // it moments earlier in the same click, making the zoom logic
          // think it was starting from "not zoomed" and turn it back on.
          stageTrack.style.transform = `translateX(-${origin * 100}%)`;
        }
      }
      stageViewport.addEventListener('pointerup', endStageDrag);
      stageViewport.addEventListener('pointercancel', endStageDrag);
    }

    window.addEventListener('resize', () => {
      goTo(current);
    });
    goTo(0);
  }

  function bindPlans(root) {
    const planInput = root.querySelector('[data-ashford-selling-plan]');
    // The offer is a (variant, plan) pair, so picking a card swaps the
    // variant too. There is no quantity field to drive any more: the
    // three-month offer is a variant holding three bottles, not one
    // bottle with the number 3 typed into a box the customer can edit.
    const variantInput = root.querySelector('[data-ashford-variant]');
    const cards = Array.from(root.querySelectorAll('[data-ashford-plan]'));
    const perDay = root.querySelector('[data-ashford-per-day]');
    const saveVs = root.querySelector('[data-ashford-save-vs]');
    const buyOnce = root.querySelector('[data-ashford-buy-once]');
    const form = root.querySelector('form.ashford-product__form');
    const atc = root.querySelector('.ashford-product__atc');
    if (!planInput) return;

    function setSellingPlan(id) {
      if (id) {
        planInput.disabled = false;
        planInput.value = id;
      } else {
        planInput.value = '';
        planInput.disabled = true;
      }
    }

    function applyCard(card) {
      cards.forEach((c) => {
        const on = c === card;
        c.classList.toggle('is-active', on);
        c.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      setSellingPlan(card.getAttribute('data-selling-plan'));
      const variantId = card.getAttribute('data-variant');
      if (variantInput && variantId) variantInput.value = variantId;
      if (perDay && card.dataset.perDay) perDay.textContent = card.dataset.perDay;
      if (saveVs && card.dataset.saveVs) saveVs.textContent = card.dataset.saveVs;

      // On mobile the add-to-cart button can sit below the fold until a
      // plan is picked — nudge it into view once one is. Deferred a frame
      // so it scrolls to the post-selection layout (per-day price text
      // etc. can change height), not the one mid-click.
      if (atc && window.matchMedia('(max-width: 989px)').matches) {
        requestAnimationFrame(() => {
          atc.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        });
      }
    }

    cards.forEach((card) => {
      card.addEventListener('click', () => applyCard(card));
    });

    if (buyOnce && form) {
      buyOnce.addEventListener('click', () => {
        const selected = root.querySelector('[data-ashford-plan].is-active');
        const restoreId = selected ? selected.getAttribute('data-selling-plan') : planInput.value;
        const restoreVariant = variantInput ? variantInput.value : null;
        // Buying once means the single bottle at its one-time price, so
        // the variant goes back to the monthly card's as well as the
        // plan being cleared — otherwise "buy once" on the 3-month card
        // charged $177 for a one-off.
        const onceCard = root.querySelector('[data-ashford-plan]');
        setSellingPlan('');
        if (variantInput && onceCard && onceCard.getAttribute('data-variant')) {
          variantInput.value = onceCard.getAttribute('data-variant');
        }
        if (typeof form.requestSubmit === 'function') form.requestSubmit(atc);
        else form.submit();
        queueMicrotask(() => {
          setSellingPlan(restoreId);
          if (variantInput && restoreVariant) variantInput.value = restoreVariant;
        });
      });
    }
  }

  function stripCartParams(params) {
    params.delete('add_to_cart');
    params.delete('plan');
    const qs = params.toString();
    const next = window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash;
    history.replaceState(null, '', next);
  }

  function addFromUrl(root) {
    const params = new URLSearchParams(window.location.search);
    const flag = (params.get('add_to_cart') || '').toLowerCase();
    if (!flag || flag === '0' || flag === 'false') return;

    const form = root.querySelector('form.ashford-product__form');
    const atc = root.querySelector('.ashford-product__atc');
    if (!form || !atc || atc.disabled) return;

    const plan = (params.get('plan') || '').toLowerCase();
    const cards = Array.from(root.querySelectorAll('[data-ashford-plan]'));
    stripCartParams(params);

    const submit = () => {
      if (typeof form.requestSubmit === 'function') form.requestSubmit(atc);
      else atc.click();
    };

    const openAndAdd = () => {
      if (plan === 'once' || plan === 'onetime' || plan === 'single') {
        const buyOnce = root.querySelector('[data-ashford-buy-once]');
        if (buyOnce) {
          buyOnce.click();
          return;
        }
      } else if (plan === '3' || plan === '90' || plan === 'quarter') {
        const card = cards.find((c) => /3-month|every 3 month/i.test(c.textContent)) || cards[1];
        if (card) card.click();
      } else if (plan === 'monthly' || plan === '1' || plan === '30') {
        const card = cards.find((c) => /monthly/i.test(c.textContent)) || cards[0];
        if (card) card.click();
      }

      submit();
    };

    if (customElements.get('cart-drawer') || !document.querySelector('cart-drawer')) {
      openAndAdd();
      return;
    }
    customElements.whenDefined('cart-drawer').then(openAndAdd);
  }

  function measureHeaderOffset() {
    const headerEl = document.querySelector('.shopify-section-group-header-group');
    const headerOffset = headerEl ? headerEl.offsetHeight : 0;
    document.documentElement.style.setProperty('--ashford-header-offset', headerOffset + 'px');
  }

  function init() {
    document.querySelectorAll('.ashford-product').forEach((root) => {
      bindGallery(root);
      bindPlans(root);
      bindZoom(root);
      addFromUrl(root);
    });
    measureHeaderOffset();
    window.addEventListener('resize', measureHeaderOffset);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
